/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { Menu, MenuHeader, MenuItem } from '@szhsin/react-menu'
import { menuHeaderSelector, menuItemSelector, menuSelector } from '@szhsin/react-menu/style-utils'
import { UnsupportedChainIdError } from '@web3-react/core'
import { NoEthereumProviderError } from '@web3-react/injected-connector'
import BigNumberJS from 'bignumber.js'
import { transparentize } from 'polished'
import { ToastOptions, toast } from 'react-toastify'
import styled, { css } from 'styled-components'

import Avatar from 'src/components/Avatar'
import FlexBox from 'src/components/Common/FlexBox'
import Icon from 'src/components/Icon'
import { useThemeContext } from 'src/contexts/ThemeContext'
import { pxToVw } from 'src/functions/units'
import { getLocalStorageDonationWillClaimCoupon } from 'src/services/donation'
import { ChainId } from 'src/types/main'

import { CHAIN_MAP, SUPPORTED_CHAINS } from '../../../constants'
import { ERROR_MESSAGE } from '../../../constants/errors'
import { useWeb3Context } from '../../../contexts/Web3Context'
import Address from '../../Address'
import { PrimaryButton } from '../../Button'
import { ToastContent } from '../../Common/Toast'

type WalletState = 'init' | 'disconnected' | 'connecting' | 'connected' | 'unsupported-chain'

const Wallet = () => {
  const { activate, active, account, deactivate } = useWeb3Context()
  const { colors } = useThemeContext()

  const ethereum = useRef((window as any).ethereum)

  const [walletState, setWalletState] = useState<WalletState>('init')

  const onActivateError = useCallback((e: Error) => {
    let toastOptions: ToastOptions = {}
    let header = ''
    let detail: ReactNode = ''
    let additionalContent = undefined
    let shouldShowUnsupportedChainToast = true

    if (e instanceof NoEthereumProviderError) {
      setWalletState('disconnected')

      toastOptions = {
        toastId: 'NoEthereumProviderError',
        type: 'error'
      }
      header = ERROR_MESSAGE.METAMASK_NOT_INSTALLED_HEADER
      detail = (
        <span dangerouslySetInnerHTML={{ __html: ERROR_MESSAGE.METAMASK_NOT_INSTALLED_DETAIL }} />
      )
    } else if (e instanceof UnsupportedChainIdError) {
      setWalletState('unsupported-chain')

      const willClaimCoupon = getLocalStorageDonationWillClaimCoupon()
      if (Number(willClaimCoupon) === 0) {
        shouldShowUnsupportedChainToast = false
      }

      toastOptions = {
        toastId: 'UnsupportedChainIdError',
        closeOnClick: false,
        autoClose: false,
        type: 'info'
      }
      header = ERROR_MESSAGE.METAMASK_CHAIN_UNSUPPORTED_HEADER
      detail = ERROR_MESSAGE.METAMASK_CHAIN_UNSUPPORTED_DETAIL
      additionalContent = (
        <details style={{ display: 'block', marginTop: 8 }}>
          <summary>
            <small>Supported chains</small>
          </summary>
          {SUPPORTED_CHAINS.map((chainId) => (
            <small style={{ display: 'block' }} key={`chain_${chainId}`}>
              {CHAIN_MAP[chainId].name} ({chainId})
            </small>
          ))}
        </details>
      )
    } else {
      // Early return without error if user has cancel the connect request
      return setWalletState('disconnected')
    }

    if (
      !(e instanceof UnsupportedChainIdError) ||
      (e instanceof UnsupportedChainIdError && shouldShowUnsupportedChainToast)
    ) {
      toast(<ToastContent message={{ header, detail, additionalContent }} />, toastOptions)
    }
  }, [])

  const handleConnect = useCallback(() => {
    setWalletState('connecting')
    activate(onActivateError)
  }, [activate, onActivateError])

  const handleDisconnectWallet = useCallback(() => {
    deactivate()
    setWalletState('disconnected')
    localStorage.setItem('connected', '0')
  }, [deactivate])

  const handleSwitchChain = useCallback((chainId: ChainId) => {
    const _ethereum = ethereum.current
    if (!_ethereum) return

    _ethereum
      .request({
        method: 'wallet_switchEthereumChain',
        params: [
          {
            chainId: `0x${new BigNumberJS(Number(chainId)).toString(16)}`
          }
        ]
      })
      .catch((error: any) => {
        console.error(error)
      })
  }, [])

  useEffect(() => {
    const connected = localStorage.getItem('connected')

    if (walletState === 'init') {
      if (connected && Number(connected) === 1) {
        return handleConnect()
      }

      if (!connected) {
        localStorage.setItem('connected', '0')
      }

      setWalletState('disconnected')
    } else if (
      (walletState === 'disconnected' || walletState === 'connecting') &&
      active &&
      account
    ) {
      localStorage.setItem('connected', '1')
      setWalletState('connected')
    } else if (walletState === 'unsupported-chain') {
      localStorage.setItem('connected', '1')
    }
  }, [account, active, handleConnect, walletState])

  const onChainChanged = useCallback(
    (chainId: string) => {
      const chainIsSupported = SUPPORTED_CHAINS.includes(Number(chainId))

      if (walletState === 'connected' && !chainIsSupported) {
        setWalletState('unsupported-chain')
        deactivate()
      } else if (walletState === 'unsupported-chain' && chainIsSupported) {
        setWalletState('connected')
        handleConnect()
      }
    },
    [deactivate, handleConnect, walletState]
  )

  const onAccountsChanged = useCallback((accounts: string[]) => {
    if (!accounts.length) {
      localStorage.setItem('connected', '0')
      setWalletState('disconnected')
    }
  }, [])

  useEffect(() => {
    const _ethereum = ethereum.current
    if (_ethereum) {
      _ethereum.on('chainChanged', onChainChanged)
      _ethereum.on('accountsChanged', onAccountsChanged)

      return () => {
        _ethereum.removeListener('chainChanged', onChainChanged)
        _ethereum.removeListener('accountsChanged', onChainChanged)
      }
    }
  }, [handleConnect, onAccountsChanged, onChainChanged])

  if (walletState === 'disconnected') {
    return (
      <PrimaryButton
        size="small"
        outlined
        onClick={walletState === 'disconnected' ? handleConnect : undefined}
      >
        Connect Wallet
      </PrimaryButton>
    )
  } else if (walletState === 'unsupported-chain') {
    return (
      <StyledMenu
        menuButton={({ open }) => (
          <StyledUnsupportedChainButton outlined size="small">
            <FlexBox as="span" alignItems="center" columnGap={8}>
              <Icon color={'#D36666'} icon="warning" size={22} />
              <span>Switch Network</span>
              <Icon icon={!open ? 'triangle-down' : 'triangle-up'} color={'#D36666'} size={14} />
            </FlexBox>
          </StyledUnsupportedChainButton>
        )}
      >
        <MenuHeader>Select a supported network</MenuHeader>
        {SUPPORTED_CHAINS.map((chainId) => (
          <MenuItem key={`chainMenu-${chainId}`} onClick={() => handleSwitchChain(chainId)}>
            <FlexBox as="span" columnGap={10} alignItems="center">
              <Avatar
                image={CHAIN_MAP[chainId].logoUri}
                circular
                size={24}
                transparentBase
                altText={`${CHAIN_MAP[chainId].name} chain logo`}
              />
              <span>{CHAIN_MAP[chainId].name}</span>
            </FlexBox>
          </MenuItem>
        ))}
      </StyledMenu>
    )
  } else if (walletState === 'connected' && active && account) {
    return (
      <>
        {/*<StyledPrimaryButton size="small" outlined $active={true}>
        <FlexBox alignItems="center" columnGap={8}>
          <Address address={account} />
          <Icon icon={!open ? 'triangle-down' : 'triangle-up'} color={colors.primary} size={14} />
        </FlexBox>
      </StyledPrimaryButton>*/}

        <StyledMenu
          menuButton={({ open }) => (
            <PrimaryButton size="small" outlined>
              <FlexBox alignItems="center" columnGap={8}>
                <Address address={account} />
                <Icon
                  icon={!open ? 'triangle-down' : 'triangle-up'}
                  color={colors.primary}
                  size={14}
                />
              </FlexBox>
            </PrimaryButton>
          )}
        >
          <MenuItem onClick={handleDisconnectWallet}>
            <FlexBox as="span" columnGap={8} alignItems="center">
              <Icon icon="disconnect" size={22} color={colors.gray600} />
              <span>Disconnect wallet</span>
            </FlexBox>
          </MenuItem>
        </StyledMenu>
      </>
    )
  }

  return (
    <PrimaryButton size="small" outlined>
      Loading...
    </PrimaryButton>
  )
}

export default Wallet

const StyledUnsupportedChainButton = styled(PrimaryButton)`
  background-color: rgba(255, 31, 31, 0.15);
  color: #d36666;
  border-color: #d36666;
`

const StyledMenu = styled(Menu)`
  /*${menuSelector.name} {
    margin-block: ${pxToVw(6)} !important;
    border-radius: ${pxToVw(7.5)};
  }*/

  ${menuSelector.name} {
    border: solid 1px rgba(184, 184, 184, 0.1);
    box-shadow: 2px 2px 3px rgba(148, 148, 148, 0.1);
    margin-block: ${pxToVw(6)} !important;
    border-radius: ${pxToVw(7.5)};
    padding-top: 0;
    padding-bottom: 0;
    // min-width: auto;
    min-width: ${pxToVw(190)};
  }

  ${menuHeaderSelector.name} {
    font-size: ${pxToVw(18)};
    color: ${({ theme }) => theme.colors.gray500};
    padding: ${pxToVw(16)} ${pxToVw(20)} ${pxToVw(4)};
    text-transform: none;
  }

  /*${menuItemSelector.name} {
    font-size: ${pxToVw(18)};
    padding: ${pxToVw(12)} ${pxToVw(20)};

    &:hover {
      background-color: ${({ theme }) => transparentize(0.9, theme.colors.primary)};
    }

    > span {
      > img {
        width: ${pxToVw(24)};
        height: ${pxToVw(24)};
        border-radius: ${pxToVw(32 / 2)};
        overflow: hidden;
      }

      > span {
        color: ${({ theme }) => theme.colors.gray700};
        font-weight: 500;
      }
    }
  }*/

  ${menuItemSelector.name} {
    font-size: ${pxToVw(17)};
    padding: ${pxToVw(15)};
    min-width: ${pxToVw(190)};

    &:hover {
      background-color: transparent;

      > span {
        > i > svg {
          fill: ${({ theme }) => theme.colors.primary};
        }
        > span {
          color: ${({ theme }) => theme.colors.primary};
        }
      }
    }

    > span {
      > span {
        color: ${({ theme }) => theme.colors.gray700};
      }
    }
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      ${menuSelector.name} {
        margin-block: ${pxToVw(6, 720)} !important;
        border-radius: ${pxToVw(7.5, 720)};
        min-width: ${pxToVw(190, 720)};
      }

      ${menuHeaderSelector.name} {
        font-size: ${pxToVw(18, 720)};
        padding: ${pxToVw(16, 720)} ${pxToVw(20, 720)} ${pxToVw(4, 720)};
      }

      ${menuItemSelector.name} {
        font-size: ${pxToVw(17, 720)};
        padding: ${pxToVw(15, 720)};
        min-width: ${pxToVw(190, 720)};
      }
    `}
  `}
`
